/**
 * Utility functions for document generation and processing
 */

import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx'
import { saveAs } from 'file-saver'
import { flattenJSON, removeReferences, extractReferences } from './contentUtils'

/**
 * Downloads content as a Word document
 * @param {Object} docContent - The content to convert to document
 * @param {string} courseTitle - The course title for filename
 */
export function downloadDocx(docContent, courseTitle) {
  try {
    const transformedData = flattenJSON(docContent)
    const doc = new Document({
      sections: [
        {
          children: transformedData.map(({ type, content }) => {
            return new Paragraph({
              spacing: {
                after: type === 'title' ? 750 : type === 'content' ? 300 : 250
              },
              children: [
                new TextRun({
                  text: content,
                  bold: true,
                  size: type === 'title' ? 28 : type === 'subtopic' ? 24 : 20,
                  color: type === 'title' ? '#0003ff' : '#000000'
                })
              ]
            })
          })
        }
      ]
    })

    saveDocumentToFile(doc, `Course Document File for ${courseTitle}.docx`)
  } catch (error) {
    console.error('Error while generating and saving the document:', error)
    // Handle the error accordingly
  }
}

/**
 * Downloads content without references as separate documents
 * @param {Object} content - The content object
 * @param {string} courseTitle - The course title for filename
 */
export const downloadWithoutReferences = (content, courseTitle) => {
  const objWithoutRef = removeReferences(content)
  const referencesArray = extractReferences(content)

  downloadRefenceDocx(referencesArray, courseTitle)
  downloadDocx(objWithoutRef, courseTitle)
}

/**
 * Downloads references as a separate Word document
 * @param {Array} array - Array of references
 * @param {string} courseTitle - The course title for filename
 */
export const downloadRefenceDocx = (array, courseTitle) => {
  try {
    const doc = new Document({
      sections: [
        {
          children: [
            new Paragraph({
              text: 'References',
              heading: HeadingLevel.HEADING_1
            }),
            ...array.map(reference => {
              return new Paragraph({
                spacing: {
                  after: 200
                },
                children: [
                  new TextRun({
                    text: reference,
                    bold: true
                  })
                ]
              })
            })
          ]
        }
      ]
    })

    saveDocumentToFile(doc, `References File for ${courseTitle}.docx`)
  } catch (error) {
    console.error('Error while generating and saving the document:', error)
  }
}

/**
 * Saves document to file using file-saver
 * @param {Document} doc - The docx document
 * @param {string} fileName - The filename
 */
export function saveDocumentToFile(doc, fileName) {
  Packer.toBlob(doc).then(blob => {
    saveAs(blob, fileName)
  })
}
